import { makeStyles } from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import clsx from 'clsx';
import React from 'react';

export type ProductDetailPageLayoutSkeletonProps = StandardDivProps<ProductDetailPageLayoutSkeletonClassKey>;

export type ProductDetailPageLayoutSkeletonClassKey = 'root';

const themeSettings = { name: 'ProductDetailPageLayoutSkeleton' };
const useStyles = makeStyles(
  (theme) => ({
    root: {},
    contentWrapper: {
      display: 'grid',
      gridColumnGap: theme.spacing(2),
      gridRowGap: theme.spacing(2),
      justifyItems: 'stretch',
      alignItems: 'stretch',
      justifyContent: 'stretch',
      gridTemplateColumns: '40% 1fr .5fr',
      gridTemplateRows: 'auto auto auto auto auto auto auto auto auto',
      gridTemplateAreas: `
          'image        name              name'
          'image        description       description'
          'image        price             addToCart'
          'image        question          stockInfo'
          'parameters   parameters        parameters'
        `,
      [theme.breakpoints.up('xl')]: {
        gridTemplateColumns: '1fr 1fr .5fr',
      },
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr 1fr .5fr',
        gridTemplateRows: '64px 350px auto 64px auto auto auto auto auto auto',
        gridTemplateAreas: `
            'name             name              name'
            'image            image             image'
            'price            addToCart         addToCart'
            'stockInfo        stockInfo         question'
            'description      description       description'
            'parameters       parameters        parameters'
          `,
        gridColumnGap: theme.spacing(1),
        gridRowGap: theme.spacing(1),
      },
      [theme.breakpoints.down('xs')]: {
        gridTemplateAreas: `
            "name             name              name"
            "image            image             image"
            "price            addToCart         addToCart"
            "stockInfo        stockInfo         stockInfo"
            "question         question          question"
            "description      description       description"
            "parameters       parameters        parameters"
          `,
        alignItems: 'center',
        justifyItems: 'center',
        gridTemplateRows: '56px auto auto auto auto auto auto auto auto',
      },

      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-columns': '40% 1fr .5fr',
      },
    },
    image: {
      gridArea: 'image',
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 1 / 4,
        '-ms-grid-column': 1,
        '-ms-grid-row-span': 4,
      },
      [theme.breakpoints.down('sm')]: {
        '-ms-grid-row': 2,
        '-ms-grid-column': 1 / 3,
      },
    },
    productName: {
      gridArea: 'name',
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 1,
        '-ms-grid-column': 2 / 3,
        '-ms-grid-column-span': 2,
      },
      [theme.breakpoints.down('sm')]: {
        '-ms-grid-column': 1 / 3,
      },
    },
    description: {
      gridArea: 'description',
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 2,
        '-ms-grid-column': 2 / 3,
        '-ms-grid-column-span': 2,
      },
      [theme.breakpoints.down('sm')]: {
        '-ms-grid-row': 5,
        '-ms-grid-column': 1 / 3,
      },
      [theme.breakpoints.down('xs')]: {
        '-ms-grid-row': 6,
      },
    },
    price: {
      gridArea: 'price',
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 3,
        '-ms-grid-column': 2,
      },
      [theme.breakpoints.down('sm')]: {
        '-ms-grid-column': 1,
      },
    },
    addToCart: {
      gridArea: 'addToCart',
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 3,
        '-ms-grid-column': 3,
      },
      [theme.breakpoints.down('sm')]: {
        '-ms-grid-column': 2 / 3,
      },
    },
    parameters: {
      gridArea: 'parameters',
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 5,
        '-ms-grid-column': 1 / 3,
        '-ms-grid-column-span': 3,
      },
      [theme.breakpoints.down('sm')]: {
        '-ms-grid-row': 6,
      },
      [theme.breakpoints.down('xs')]: {
        '-ms-grid-row': 7,
      },
    },
    question: {
      gridArea: 'question',
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 4,
        '-ms-grid-column': 2,
      },
      [theme.breakpoints.down('sm')]: {
        '-ms-grid-row': 4,
        '-ms-grid-column': 3,
      },
      [theme.breakpoints.down('sm')]: {
        '-ms-grid-row': 5,
        '-ms-grid-column': 1 / 3,
      },
    },
    stockInfo: {
      gridArea: 'stockInfo',
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center',
      paddingBottom: theme.spacing(1),
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 4,
        '-ms-grid-column': 3,
      },
      [theme.breakpoints.down('sm')]: {
        '-ms-grid-row': 4,
        '-ms-grid-column': 1 / 2,
      },
      [theme.breakpoints.down('xs')]: {
        '-ms-grid-column': 1 / 3,
      },
    },
  }),
  themeSettings,
);

const ProductDetailPageLayoutSkeleton = React.forwardRef<HTMLDivElement, ProductDetailPageLayoutSkeletonProps>(
  ({ className, classes: pClasses }, ref) => {
    const classes = useStyles({ classes: pClasses });

    return (
      <div ref={ref} className={clsx(classes.root, className)}>
        <Skeleton variant='text' height={'4rem'} width={'100%'} />
        <div className={classes.contentWrapper}>
          <Skeleton variant='rect' width={'100%'} height={300} component='div' className={classes.image} />
          <Skeleton variant='rect' width={'100%'} height={64} component='div' className={classes.productName} />
          <Skeleton variant='rect' width={'100%'} height={48} component='div' className={classes.description} />
          <Skeleton variant='rect' width={'100%'} height={80} component='div' className={classes.price} />
          <Skeleton variant='rect' width={'100%'} height={56} component='div' className={classes.stockInfo} />
          <Skeleton variant='rect' width={'100%'} height={56} component='div' className={classes.addToCart} />
          <Skeleton variant='rect' width={'100%'} height={56} component='div' className={classes.question} />
          <Skeleton variant='rect' width={'100%'} height={100} component='div' className={classes.parameters} />
        </div>
      </div>
    );
  },
);

ProductDetailPageLayoutSkeleton.displayName = 'ProductDetailPageLayoutSkeleton';

export default withThemeProps(themeSettings)(ProductDetailPageLayoutSkeleton);
